import { ExtensionOutlined } from "@mui/icons-material";
import {
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { Command, SquareTerminal } from "lucide-react";
import { PostHogFeature } from "posthog-js/react";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ROUTE_SLUGS } from "src/config/navigation/routes";
import { useGetAssistantProcesses } from "src/hooks/queries/assistant-process";
import { AssistantProcessStatus } from "src/stores/models/assistant-process";
import { fillRouteWithSlugs } from "src/utils/navigation";
import { ROUTES } from "../../config";
import { SidebarSectionHeader } from "../Layout/SidebarSectionHeader";

interface DeviceNavbarProps {
  collapseRequestKey?: number;
  isCollapsed?: boolean;
  deviceIdOverride?: string;
}

const DeviceNavbar = ({
  collapseRequestKey = 0,
  isCollapsed = false,
  deviceIdOverride,
}: DeviceNavbarProps) => {
  const [isToolsExpanded, setIsToolsExpanded] = useState(
    collapseRequestKey === 0
  );
  const { pathname } = useLocation();
  const { orgId = "", deviceId: routeDeviceId = "" } = useParams();
  const deviceId = deviceIdOverride ?? routeDeviceId;
  const hasDeviceTarget = Boolean(deviceId);

  const { data: assistantProcesses } = useGetAssistantProcesses({
    orgId,
    deviceId,
    status: AssistantProcessStatus.IN_PROGRESS,
    enabled: !!orgId && hasDeviceTarget,
  });

  const [assistantProcessUrl, setAssistantProcessUrl] = useState<string>(
    ROUTES.ASSISTANT
  );
  const navItemHoverSx = {
    marginRight: 0,
    "&:hover": {
      bgcolor: "rgba(0, 0, 0, 0.04)",
    },
  };
  const drawerListItemIconSx = { color: "rgba(0, 0, 0, 0.54)" };
  const skillsUrl = fillRouteWithSlugs(ROUTES.SKILLS, {
    [ROUTE_SLUGS.ORG_ID]: orgId,
  });
  const isToolsSectionExpanded = isToolsExpanded;
  const noDeviceToolsTooltip = "Create or select a device to use tools";

  const renderToolButton = ({
    icon,
    key,
    label,
    route,
    testId,
    tooltipTargetTestId,
  }: {
    icon: ReactNode;
    key: string;
    label: string;
    route: ROUTES | string;
    testId: string;
    tooltipTargetTestId: string;
  }) => {
    const toolButtonContent = (
      <>
        <ListItemIcon
          className={"min-w-fit my-[2px]"}
          sx={drawerListItemIconSx}
        >
          {icon}
        </ListItemIcon>
        <ListItemText primary={label} />
      </>
    );

    if (!hasDeviceTarget) {
      return (
        <Tooltip key={key} title={noDeviceToolsTooltip} placement="right">
          <span className="block" data-testid={tooltipTargetTestId}>
            <ListItemButton
              data-testid={testId}
              disabled
              className={"gap-4"}
              sx={{ ...navItemHoverSx }}
            >
              {toolButtonContent}
            </ListItemButton>
          </span>
        </Tooltip>
      );
    }

    return (
      <Tooltip
        key={key}
        title={label}
        placement="right"
        disableHoverListener={!isCollapsed}
      >
        <ListItemButton
          data-testid={testId}
          className={"gap-4"}
          component={Link}
          to={fillRouteWithSlugs(route as ROUTES, {
            [ROUTE_SLUGS.ORG_ID]: orgId,
            [ROUTE_SLUGS.DEVICE_ID]: deviceId,
          })}
          state={{ previous: pathname }}
          sx={{ ...navItemHoverSx }}
        >
          {toolButtonContent}
        </ListItemButton>
      </Tooltip>
    );
  };

  useEffect(() => {
    if (collapseRequestKey > 0) {
      setIsToolsExpanded(false);
    }
  }, [collapseRequestKey]);

  useEffect(() => {
    const shouldResetAssistantProcess =
      assistantProcesses?.[0]?.status === AssistantProcessStatus.CANCELLED ||
      assistantProcesses?.[0]?.status === AssistantProcessStatus.COMPLETED;

    if (shouldResetAssistantProcess) {
      setAssistantProcessUrl(ROUTES.ASSISTANT);
    } else {
      setAssistantProcessUrl(
        assistantProcesses?.[0]?.state?.currentPath || ROUTES.ASSISTANT
      );
    }
  }, [assistantProcesses, orgId, deviceId]);

  return (
    <List key="device-list" disablePadding>
      <SidebarSectionHeader
        isCollapsed={isCollapsed}
        isExpanded={isToolsSectionExpanded}
        onToggle={() => setIsToolsExpanded((current) => !current)}
        title="Tools"
      />

      <Collapse in={isToolsSectionExpanded}>
        <Tooltip
          title="Skills"
          placement="right"
          disableHoverListener={!isCollapsed}
        >
          <ListItemButton
            key="skills"
            className={"gap-4"}
            component={Link}
            selected={skillsUrl === pathname}
            to={skillsUrl}
            state={{ previous: pathname }}
            sx={{ ...navItemHoverSx }}
          >
            <ListItemIcon
              className={"min-w-fit my-[2px]"}
              sx={drawerListItemIconSx}
            >
              <ExtensionOutlined sx={{ fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText primary={"Skills"} />
          </ListItemButton>
        </Tooltip>

        {renderToolButton({
          icon: <Command className="h-5 w-5" />,
          key: "Assistant",
          label: "Assistant",
          route: assistantProcessUrl,
          testId: "assistant-nav-button",
          tooltipTargetTestId: "assistant-nav-tooltip-target",
        })}

        <PostHogFeature flag="terminal-in-menu" match={true}>
          {renderToolButton({
            icon: <SquareTerminal className="h-5 w-5" />,
            key: "terminal",
            label: "Terminal",
            route: ROUTES.TERMINAL,
            testId: "terminal-nav-button",
            tooltipTargetTestId: "terminal-nav-tooltip-target",
          })}
        </PostHogFeature>
      </Collapse>
    </List>
  );
};

export default DeviceNavbar;
